/**
 * /winmail-dat — the page.
 *
 * A winmail.dat is read where it lies: headers are parsed, attachment bytes
 * are not. Each attachment stays a byte range of the file the person picked
 * and becomes a Blob slice only at the moment they save it, so a 300 MB video
 * inside a winmail.dat costs nothing until it is asked for.
 *
 * The unit of the daily cap is the FILE the person brought: reading it, seeing
 * the letter and the list of attachments is free; the first file that is
 * actually saved from it spends the day's unit, and everything else taken from
 * the same winmail.dat that day is free (one key for all of them).
 */

import { ByteSource, parseTnef, TNEF_SIGNATURE, safeFileName, extOf, decoderFor } from './tnef-reader.js'
import { resolveBody } from './letter.js'
import { buildIcs } from './ics.js'
import { suggestOtherTool } from '../file-signature.js'
import { reportShape } from '../file-shape.js'
import { parseMessage } from '../mail-archive/mail-parse.js'

const SLUG = 'winmail-dat'
const I18N = window.__WM_I18N || {}
const t = (k, fb, params) => {
  let s = I18N[k] != null ? String(I18N[k]) : (fb || k)
  if (params) for (const [p, v] of Object.entries(params)) s = s.split(':' + p).join(String(v))
  return s
}
const $ = (s) => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}
/* The page has already said what happened in its own status card: the error
   is recorded with its place, and the shared toast stays quiet. */
function logTold(e, label) {
  try { if (e && typeof e === 'object') e.__userNotified = true } catch (_) { /* frozen error */ }
  try { window.__handleError && window.__handleError(e, label) } catch (_) { /* telemetry never breaks the tool */ }
}
const stage = (name, extra) => { try { window.__stage && window.__stage(name, extra) } catch (_) { /* telemetry never breaks the tool */ } }

function fmtBytes(n) {
  if (!(n >= 0)) return ''
  if (n < 1024) return n + ' ' + t('unit_b', 'B')
  if (n < 1048576) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + ' ' + t('unit_kb', 'KB')
  if (n < 1073741824) return (n / 1048576).toFixed(n < 10485760 ? 1 : 0) + ' ' + t('unit_mb', 'MB')
  return (n / 1073741824).toFixed(1) + ' ' + t('unit_gb', 'GB')
}

function fmtDate(d) {
  if (!d) return ''
  try {
    const loc = typeof window.__intlLocale === 'function' ? window.__intlLocale() : undefined
    return new Intl.DateTimeFormat(loc, { dateStyle: 'long', timeStyle: 'short' }).format(d)
  } catch (_) { return d.toISOString().slice(0, 16).replace('T', ' ') }
}

const state = { file: null, key: null, msg: null, urls: [] }

/* ── delivery: the only place a unit is spent ───────────────────────────── */

function revokeLater(u) { state.urls.push(u) }
function releaseUrls() { for (const u of state.urls) { try { URL.revokeObjectURL(u) } catch (_) {} } state.urls = [] }

/** Reserve the unit, hand the result over, settle by what actually happened. */
async function deliver(produce) {
  const lim = window.__toolLimit
  const run = lim ? lim.begin(SLUG, state.key) : null
  if (lim && !run) return false                       // the wall is shown by begin()
  try {
    const ok = await produce()
    if (run) { if (ok) run.commit(); else run.abort() }
    return ok
  } catch (e) {
    if (run) run.abort()
    if (window.__isUserCancel && window.__isUserCancel(e)) return false
    if (window.__handleError) window.__handleError(e, 'winmail_save', { say: t('err_save', 'The file could not be saved.') })
    return false
  }
}

function saveBlob(blob, name) {
  return deliver(async () => {
    if (window.__downloadFile) return (await window.__downloadFile(blob, name)) !== false
    const u = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = u
    a.download = name
    a.click()
    setTimeout(() => URL.revokeObjectURL(u), 4000)
    return true
  })
}

/* ── attachments ────────────────────────────────────────────────────────── */

function attachmentName(a, i) {
  let name = a.name
  if (!name) name = t('unnamed', 'attachment-:n', { n: i + 1 }) + (a.ext ? '.' + a.ext : '')
  return safeFileName(name, 'attachment-' + (i + 1))
}

function attachmentBlob(msg, a) {
  if (!a.data) return null
  return msg.src.blob(a.data.off, a.data.len, a.mime || 'application/octet-stream')
}

const PREVIEW_IMAGE = /^image\/(jpeg|png|gif|webp|bmp)$/
const PREVIEW_TEXT = new Set(['txt', 'log', 'csv', 'xml', 'json', 'ics', 'vcf', 'htm', 'html', 'ini', 'cfg', 'md', 'bat', 'sys', 'src'])

function previewKind(a) {
  if (!a.data || !a.size) return ''
  if (PREVIEW_IMAGE.test(a.mime || '')) return a.size <= 40 << 20 ? 'image' : ''
  const ext = extOf(a.name)
  if (ext === 'pdf' || a.mime === 'application/pdf') return 'pdf'
  if (PREVIEW_TEXT.has(ext) || /^text\//.test(a.mime || '')) return 'text'
  return ''
}

async function showPreview(msg, a, host) {
  host.replaceChildren()
  const kind = previewKind(a)
  const blob = attachmentBlob(msg, a)
  if (!blob) return
  if (kind === 'image') {
    const u = URL.createObjectURL(blob)
    revokeLater(u)
    const img = el('img', 'wm-prev-img')
    img.alt = a.name || ''
    img.src = u
    host.append(img)
  } else if (kind === 'pdf') {
    const u = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
    revokeLater(u)
    const f = el('iframe', 'wm-prev-pdf')
    f.title = a.name || 'PDF'
    f.src = u
    host.append(f)
  } else if (kind === 'text') {
    const head = new Uint8Array(await blob.slice(0, 64 * 1024).arrayBuffer())
    // A text attachment carries no charset of its own: UTF-8 if it is valid UTF-8,
    // otherwise the code page the sender's Outlook declared for the stream.
    let s
    try { s = new TextDecoder('utf-8', { fatal: true }).decode(head) } catch (_) { s = decoderFor(msg.codepage || 1252).decode(head) }
    const pre = el('pre', 'wm-prev-text', s + (a.size > head.length ? '\n…' : ''))
    host.append(pre)
  }
}

function typeLabel(a) {
  if (a.kind === 'message') return t('kind_message', 'Letter')
  if (a.kind === 'ole' || a.kind === 'object') return t('kind_ole', 'Embedded object')
  const ext = extOf(a.name)
  return ext ? ext.toUpperCase() : (a.mime || t('kind_file', 'File'))
}

function renderAttachments(msg, host, depth) {
  const atts = msg.attachments
  const files = atts.filter((a) => a.data && a.kind !== 'message')
  const box = el('section', 'wm-block wm-atts')
  const head = el('div', 'wm-atts-head')
  head.append(el('h3', null, t('atts_title', 'Attachments') + ' · ' + atts.length))
  if (files.length > 1) {
    const all = el('button', 'wm-btn')
    all.type = 'button'
    all.textContent = t('save_all', 'Download all (:n)', { n: files.length })
    all.addEventListener('click', () => saveAll(msg, files))
    head.append(all)
  }
  box.append(head)

  atts.forEach((a, i) => {
    const row = el('div', 'wm-att')
    const main = el('div', 'wm-att-main')
    const name = a.kind === 'message'
      ? (a.embedded && a.embedded.subject) || t('embedded_untitled', 'Letter without a subject')
      : (a.name || t('unnamed', 'attachment-:n', { n: i + 1 }) + (a.ext ? '.' + a.ext : ''))
    main.append(el('span', 'wm-att-name', name))
    const meta = [typeLabel(a), fmtBytes(a.size)]
    if (a.hidden || (a.contentId && /^image\//.test(a.mime || ''))) meta.push(t('inline', 'picture inside the letter'))
    if (a.checksumBad) meta.push(t('att_damaged', 'damaged in transit'))
    if (a.shortName && a.name && a.shortName !== a.name && a.kind === 'file') meta.push(a.shortName)
    main.append(el('span', 'wm-att-meta', meta.filter(Boolean).join(' · ')))
    row.append(main)

    const acts = el('div', 'wm-att-acts')
    const prevHost = el('div', 'wm-prev')
    prevHost.hidden = true
    if (a.kind === 'message') {
      if (a.embedded) {
        const open = el('button', 'wm-mini')
        open.type = 'button'
        open.textContent = t('open_letter', 'Open the letter')
        open.setAttribute('aria-expanded', 'false')
        let built = false
        open.addEventListener('click', () => {
          if (!built) { renderLetter(a.embedded, prevHost, depth + 1); built = true }
          prevHost.hidden = !prevHost.hidden
          open.setAttribute('aria-expanded', String(!prevHost.hidden))
          open.textContent = prevHost.hidden ? t('open_letter', 'Open the letter') : t('hide', 'Hide')
        })
        acts.append(open)
      } else {
        main.append(el('span', 'wm-att-meta wm-warn', t('embedded_unreadable', 'The letter inside could not be read.')))
      }
    } else if (a.data) {
      if (previewKind(a)) {
        const pv = el('button', 'wm-mini')
        pv.type = 'button'
        pv.textContent = t('preview', 'Preview')
        pv.addEventListener('click', async () => {
          if (prevHost.hidden) {
            prevHost.hidden = false
            pv.textContent = t('hide', 'Hide')
            try { await showPreview(msg, a, prevHost) } catch (e) { prevHost.textContent = t('preview_failed', 'No preview for this file.') }
            stage('preview', { kind: previewKind(a) })
          } else {
            prevHost.hidden = true
            prevHost.replaceChildren()
            pv.textContent = t('preview', 'Preview')
          }
        })
        acts.append(pv)
      }
      const dl = el('button', 'wm-mini wm-mini-accent')
      dl.type = 'button'
      dl.textContent = t('save', 'Download')
      dl.disabled = !a.size
      if (!a.size) dl.title = t('att_empty', 'This attachment is empty in the file')
      dl.addEventListener('click', () => {
        const blob = attachmentBlob(msg, a)
        if (blob) saveBlob(blob, attachmentName(a, i))
      })
      acts.append(dl)
      if (!a.size) main.append(el('span', 'wm-att-meta wm-warn', t('att_empty', 'This attachment is empty in the file')))
    } else {
      main.append(el('span', 'wm-att-meta wm-warn', t('att_no_data', 'Only the name of this attachment was sent; its contents are not in the file.')))
    }
    row.append(acts)
    box.append(row)
    box.append(prevHost)
  })
  host.append(box)
}

function saveAll(msg, files) {
  return deliver(async () => {
    const bd = window.__batchDeliver
    const items = files.map((a) => ({ name: attachmentName(a, msg.attachments.indexOf(a)), blob: attachmentBlob(msg, a) }))
    if (!bd || typeof bd.deliver !== 'function') {
      // The batch door is a tag on this page; without it, the first file only —
      // one file per gesture is the browser's rule, not ours.
      if (window.__toast) window.__toast(t('save_all_unavailable', 'Saving all at once is not available here — download the files one by one.'), 'warning')
      return false
    }
    const zipName = safeFileName((msg.subject || 'winmail').slice(0, 60), 'winmail') + '.zip'
    const r = await bd.deliver(items, { zipName, slug: SLUG })
    return !!(r && r.written)
  })
}

/* ── the letter ─────────────────────────────────────────────────────────── */

function personLine(p) {
  if (!p) return ''
  if (p.name && p.email && p.name !== p.email) return `${p.name} <${p.email}>`
  return p.name || p.email || ''
}

let purifyReady = null
function loadPurify() {
  if (window.DOMPurify) return Promise.resolve(window.DOMPurify)
  if (!purifyReady) {
    const src = window.__WM_PURIFY || []
    purifyReady = (window.__vendorScript ? window.__vendorScript(src, { expect: 'DOMPurify', label: 'dompurify' }) : Promise.reject(new Error('no_vendor_door')))
      .then(() => window.DOMPurify)
  }
  return purifyReady
}

/* Inline pictures of the letter (cid:) become data: URLs — the frame admits
   nothing else, so a remote image in a stranger's letter is never fetched. */
async function inlineImages(msg, doc) {
  const imgs = doc.querySelectorAll('img[src^="cid:" i]')
  if (!imgs.length) return
  const byCid = new Map()
  for (const a of msg.attachments) if (a.contentId && a.data) byCid.set(a.contentId.toLowerCase(), a)
  for (const img of imgs) {
    const cid = decodeURIComponent(img.getAttribute('src').slice(4)).toLowerCase()
    const a = byCid.get(cid) || [...byCid.entries()].find(([k]) => k.split('@')[0] === cid.split('@')[0])?.[1]
    if (!a || a.size > 8 << 20 || !/^image\//.test(a.mime || 'image/')) { img.removeAttribute('src'); continue }
    const bytes = new Uint8Array(await attachmentBlob(msg, a).arrayBuffer())
    let bin = ''
    for (let i = 0; i < bytes.length; i += 32768) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 32768))
    img.setAttribute('src', `data:${a.mime || 'image/png'};base64,${btoa(bin)}`)
  }
}

const FRAME_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:"

async function htmlFrame(msg, html) {
  const DOMPurify = await loadPurify()
  if (!DOMPurify || typeof DOMPurify.sanitize !== 'function') throw new Error('purify_missing')
  const clean = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true, RETURN_DOM: false,
    FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'iframe', 'frame', 'object', 'embed', 'base', 'link', 'meta'],
    FORBID_ATTR: ['srcset', 'background', 'ping', 'formaction'],
  })
  const doc = new DOMParser().parseFromString(clean, 'text/html')
  await inlineImages(msg, doc)
  for (const n of doc.querySelectorAll('[src]')) { if (!/^data:/i.test(n.getAttribute('src'))) n.removeAttribute('src') }
  for (const a of doc.querySelectorAll('a[href]')) { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer') }
  const frame = el('iframe', 'wm-body-frame')
  frame.title = t('body_frame_title', 'Letter text')
  // No scripts, no same-origin, no network; links open in a new tab.
  frame.setAttribute('sandbox', 'allow-popups allow-popups-to-escape-sandbox')
  frame.setAttribute('referrerpolicy', 'no-referrer')
  // Outlook's own class styles (MsoNormal…) live in <head>; they come along.
  // A url() inside them fetches nothing: the frame's policy admits data: only.
  const headStyles = doc.head ? [...doc.head.querySelectorAll('style')].map((s) => s.outerHTML).join('') : ''
  frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}"><style>body{font:14px/1.55 system-ui,-apple-system,Segoe UI,Arial,sans-serif;color:#111;background:#fff;margin:14px;word-wrap:break-word}img{max-width:100%;height:auto}table{max-width:100%}</style>${headStyles}</head><body>${doc.body ? doc.body.innerHTML : ''}</body></html>`
  return frame
}

function meetingBox(msg) {
  const m = msg.meeting
  const box = el('div', 'wm-meeting')
  box.append(el('h4', null, t('meeting_' + m.kind, t('meeting_meeting', 'Meeting'))))
  const dl = el('dl', 'wm-dl')
  const row = (k, v) => { if (!v) return; dl.append(el('dt', null, k), el('dd', null, v)) }
  const when = m.start ? fmtDate(m.start) + (m.end ? ' — ' + fmtDate(m.end) : '') : (m.startLocal ? m.startLocal + (m.endLocal ? ' — ' + m.endLocal : '') : '')
  row(t('meeting_when', 'When'), when)
  row(t('meeting_where', 'Where'), m.location)
  row(t('meeting_organizer', 'Organizer'), personLine(m.organizer))
  box.append(dl)
  if (m.recurring) box.append(el('p', 'wm-note-line', t('meeting_recurring', 'This is a recurring meeting; the calendar file holds its first occurrence.')))
  if (m.start && (m.kind === 'request' || m.kind === 'meeting')) {
    const b = el('button', 'wm-btn')
    b.type = 'button'
    b.textContent = t('save_ics', 'Add to calendar (.ics)')
    b.addEventListener('click', () => {
      const body = resolveBody(msg)
      const ics = buildIcs(msg, body.text || body.rtfText || '')
      saveBlob(new Blob([ics], { type: 'text/calendar' }), safeFileName((msg.subject || 'meeting').slice(0, 60), 'meeting') + '.ics')
    })
    box.append(b)
  }
  return box
}

async function renderBody(msg, host) {
  const body = resolveBody(msg)
  const wrap = el('div', 'wm-body')
  host.append(wrap)
  const textOf = body.text || body.rtfText
  const showText = (s) => { wrap.replaceChildren(el('pre', 'wm-body-text', s || t('body_none', '(the letter has no text)'))) }

  if (body.html) {
    const tabs = el('div', 'wm-tabs')
    const bHtml = el('button', 'wm-tab is-on', t('body_html', 'As sent'))
    const bText = el('button', 'wm-tab', t('body_text', 'Plain text'))
    bHtml.type = bText.type = 'button'
    tabs.append(bHtml, bText)
    host.insertBefore(tabs, wrap)
    const plain = textOf || htmlToText(body.html)
    let frame = null
    const toHtml = async () => {
      bHtml.classList.add('is-on'); bText.classList.remove('is-on')
      try {
        if (!frame) frame = await htmlFrame(msg, body.html)
        wrap.replaceChildren(frame)
        wrap.append(el('p', 'wm-note-line', t('html_note', 'Pictures from the internet are not loaded; pictures sent with the letter are shown.')))
      } catch (e) {
        showText(plain)
        wrap.prepend(el('p', 'wm-note-line', t('html_unavailable', 'The formatted version could not be shown here; this is its text.')))
      }
    }
    bHtml.addEventListener('click', toHtml)
    bText.addEventListener('click', () => { bText.classList.add('is-on'); bHtml.classList.remove('is-on'); showText(plain) })
    await toHtml()
  } else {
    showText(textOf)
  }

  if (body.rtfKind === 'rtf' && body.rtf) {
    const note = el('p', 'wm-note-line', t('rtf_note', 'The letter was written in Outlook’s rich text format. Its formatting is kept in the .rtf file, which Word and LibreOffice open.'))
    const b = el('button', 'wm-mini wm-mini-accent', t('save_rtf', 'Letter as .rtf'))
    b.type = 'button'
    b.addEventListener('click', () => saveBlob(new Blob([body.rtf], { type: 'application/rtf' }), safeFileName((msg.subject || 'letter').slice(0, 60), 'letter') + '.rtf'))
    host.append(note, b)
  }
  if (body.rtfDamaged && !body.html && !body.text) host.append(el('p', 'wm-note-line wm-warn', t('rtf_damaged', 'The letter text is damaged in the file; what could be read is shown.')))
  return body
}

function htmlToText(html) {
  try {
    const d = new DOMParser().parseFromString(html, 'text/html')
    for (const s of d.querySelectorAll('script,style,head')) s.remove()
    return (d.body ? d.body.innerText || d.body.textContent : '').replace(/\n{3,}/g, '\n\n').trim()
  } catch (_) { return '' }
}

const CLASS_LABEL = [
  [/^IPM\.Schedule\.Meeting\.Request/i, 'class_meeting_request'],
  [/^IPM\.Schedule\.Meeting\.Canceled/i, 'class_meeting_cancel'],
  [/^IPM\.Schedule\.Meeting\.Resp/i, 'class_meeting_response'],
  [/^IPM\.Appointment/i, 'class_appointment'],
  [/^IPM\.Task/i, 'class_task'],
  [/^IPM\.Contact/i, 'class_contact'],
  [/^IPM\.DistList/i, 'class_distlist'],
  [/^IPM\.StickyNote/i, 'class_note'],
  [/^Report\./i, 'class_report'],
  [/Voicemail/i, 'class_voicemail'],
]

function renderLetter(msg, host, depth) {
  const card = el('article', depth ? 'wm-letter wm-letter-nested' : 'wm-letter')
  const head = el('header', 'wm-letter-head')
  const cls = CLASS_LABEL.find(([re]) => re.test(msg.messageClass || ''))
  if (cls) head.append(el('span', 'wm-badge', t(cls[1], msg.messageClass)))
  head.append(el(depth ? 'h4' : 'h3', 'wm-subject', msg.subject || t('no_subject', '(no subject)')))
  const dl = el('dl', 'wm-dl')
  const row = (k, v) => { if (!v) return; dl.append(el('dt', null, k), el('dd', null, v)) }
  row(t('from', 'From'), personLine(msg.from))
  row(t('to', 'To'), msg.to.length ? msg.to.map(personLine).join(', ') : msg.displayTo)
  row(t('cc', 'Cc'), msg.cc.length ? msg.cc.map(personLine).join(', ') : msg.displayCc)
  if (msg.sent || msg.sentLocal) row(t('date', 'Sent'), msg.sent ? fmtDate(msg.sent) : msg.sentLocal)
  else row(t('date_received', 'Received'), msg.received ? fmtDate(msg.received) : msg.receivedLocal)
  if (dl.childElementCount) head.append(dl)
  card.append(head)
  if (msg.meeting) card.append(meetingBox(msg))
  const bodyHost = el('div', 'wm-body-host')
  card.append(bodyHost)
  renderBody(msg, bodyHost).catch((e) => {
    bodyHost.append(el('p', 'wm-note-line wm-warn', t('body_failed', 'The letter text could not be read.')))
    logTold(e, 'winmail_body')
  })
  if (msg.attachments.length) renderAttachments(msg, card, depth)
  host.append(card)
}

/* ── finding the TNEF in what was dropped ───────────────────────────────── */

const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0
const asciiHead = (b, n) => { let s = ''; for (let i = 0; i < Math.min(n, b.length); i++) s += String.fromCharCode(b[i]); return s }

function base64Bytes(text) {
  const clean = text.replace(/[^A-Za-z0-9+/=]/g, '')
  const bin = atob(clean)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** What the dropped file is and where its TNEF stream sits. */
async function locate(file) {
  const head = new Uint8Array(await file.slice(0, 64 * 1024).arrayBuffer())
  if (head.length >= 4 && u32(head, 0) === TNEF_SIGNATURE) return { kind: 'tnef', src: new ByteSource(file) }

  const text = asciiHead(head, 4096)
  // The part saved as text: base64 of a TNEF stream starts with "eJ8+I".
  if (/^\s*eJ8\+I[A-Za-z0-9+/]/.test(text) && file.size < 512 << 20) {
    const bytes = base64Bytes(await file.text())
    if (u32(bytes, 0) === TNEF_SIGNATURE) return { kind: 'tnef', src: new ByteSource(bytes), via: 'base64' }
  }
  // A whole letter (.eml) with winmail.dat inside it.
  if (/^(?:[\x21-\x39\x3B-\x7E]+:[^\r\n]*\r?\n(?:[ \t][^\r\n]*\r?\n)*){2,}/.test(text) && /(^|\n)(content-type|mime-version|received|from|subject|date):/i.test(text)) {
    if (file.size > 400 << 20) return { kind: 'eml', tooBig: true }
    const bytes = new Uint8Array(await file.arrayBuffer())
    const m = parseMessage(bytes)
    const part = (m.attachments || []).find((a) => /ms-tnef/i.test(a.type || '') || (a.bytes && a.bytes.length > 4 && u32(a.bytes, 0) === TNEF_SIGNATURE))
    if (part && part.bytes && u32(part.bytes, 0) === TNEF_SIGNATURE) {
      return { kind: 'tnef', src: new ByteSource(part.bytes), via: 'eml', outer: m }
    }
    return { kind: 'eml' }
  }
  // OLE compound file: an Outlook .msg carries "__substg1.0_" stream names.
  if (head.length >= 8 && head[0] === 0xD0 && head[1] === 0xCF && head[2] === 0x11 && head[3] === 0xE0) {
    const probe = new Uint8Array(await file.slice(0, Math.min(file.size, 4 << 20)).arrayBuffer())
    const needle = '__substg1.0_'
    let hit = false
    outer: for (let i = 0; i + needle.length * 2 < probe.length; i += 2) {
      for (let k = 0; k < needle.length; k++) if (probe[i + k * 2] !== needle.charCodeAt(k) || probe[i + k * 2 + 1] !== 0) continue outer
      hit = true
      break
    }
    if (hit || /\.msg$/i.test(file.name || '')) return { kind: 'msg' }
  }
  return { kind: 'other' }
}

/* ── status card ────────────────────────────────────────────────────────── */

function status(title, body, tone, extra) {
  const box = $('#wm-status')
  box.hidden = false
  box.className = 'wm-status wm-tone-' + (tone || 'info')
  box.replaceChildren()
  if (title) box.append(el('h3', 'wm-status-title', title))
  if (body) box.append(el('p', 'wm-status-body', body))
  if (extra) box.append(extra)
  return box
}

function link(href, text) {
  const p = el('p', 'wm-status-body')
  const a = el('a', null, text)
  a.href = href
  p.append(a)
  return p
}

function clear() {
  releaseUrls()
  $('#wm-out').replaceChildren()
  $('#wm-out').hidden = true
  $('#wm-status').hidden = true
  state.msg = null
}

function countAll(msg) {
  let files = 0, letters = 0
  for (const a of msg.attachments) {
    if (a.kind === 'message') { letters++; if (a.embedded) { const c = countAll(a.embedded); files += c.files; letters += c.letters } } else files++
  }
  return { files, letters }
}

async function open(file) {
  clear()
  state.file = file
  try { state.key = window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null } catch (_) { state.key = null }
  status('', t('reading', 'Reading the file…'), 'info')
  let src = file
  try { if (window.FileBytes && typeof window.FileBytes.settle === 'function') src = (await window.FileBytes.settle(file)) || file } catch (_) { src = file }

  let where
  try { where = await locate(src) } catch (e) {
    status(t('err_read_title', 'The file could not be read'), t('err_read', 'The browser could not read this file. Pick it again — if it sits in a cloud folder, download it to the device first.'), 'bad')
    logTold(e, 'winmail_locate')
    stage('error', { at: 'read' })
    return
  }

  if (where.kind !== 'tnef') {
    stage('wrong_file', { what: where.kind })
    try { reportShape({ format: 'tnef', ok: false, size: file.size, reason: 'not-' + where.kind }) } catch (_) {}
    if (where.kind === 'eml') {
      const box = status(t('verdict_eml', 'This is a letter without winmail.dat inside'),
        where.tooBig ? t('body_eml_big', 'This letter file is too large to look through here.') : t('body_eml', 'The letter has no winmail.dat part. Its text and attachments open in the mail archive reader.'), 'warn')
      box.append(link(`/${window.__LOCALE || 'en'}/mail-archive`, t('eml_link', 'Open it in the mail archive reader')))
      return
    }
    if (where.kind === 'msg') {
      status(t('verdict_msg', 'This is an Outlook item (.msg), not winmail.dat'), t('body_msg', 'A .msg file is a saved Outlook message in a different format. This page opens winmail.dat — the attachment Outlook sends in place of a letter’s files.'), 'warn')
      return
    }
    const box = status(t('verdict_not_tnef', 'This is not winmail.dat'), t('body_not_tnef', 'A winmail.dat file always begins with the same four bytes, and this one does not. If the letter came with several attachments, look for the one named winmail.dat or ATT00001.dat.'), 'bad')
    suggestOtherTool(file, SLUG, box, { text: t('suggest_text', 'This looks like a :format file.'), link: t('suggest_link', 'Open it with the right tool') })
    return
  }

  stage('parsing', { via: where.via || 'file', mb: Math.round(file.size / 1048576) })
  let msg
  try {
    msg = await parseTnef(where.src)
  } catch (e) {
    status(t('verdict_broken', 'winmail.dat could not be read'), t('body_broken', 'The file starts like winmail.dat but its contents are damaged beyond reading.'), 'bad')
    try { reportShape({ format: 'tnef', ok: false, size: file.size, reason: String(e && e.code || 'parse').slice(0, 30) }) } catch (_) {}
    logTold(e, 'winmail_parse')
    stage('error', { at: 'parse' })
    return
  }
  state.msg = msg
  if (where.outer && !msg.subject) {
    // The MIME letter carries the subject and sender; TNEF inside it usually does not.
    msg.subject = where.outer.subject || ''
    if (!msg.from && where.outer.from) msg.from = { name: where.outer.from, email: '' }
  }

  const c = countAll(msg)
  const body = resolveBody(msg)
  const hasText = !!(body.html || body.text || body.rtfText)
  const out = $('#wm-out')
  out.hidden = false

  const notes = el('div', 'wm-notes')
  if (where.via === 'eml') notes.append(el('p', 'wm-note-line', t('note_from_eml', 'winmail.dat was found inside the letter and opened.')))
  if (where.via === 'base64') notes.append(el('p', 'wm-note-line', t('note_base64', 'The file was saved as text (base64); it has been decoded.')))
  if (msg.stoppedAt === 'truncated') notes.append(el('p', 'wm-note-line wm-warn', t('note_truncated', 'The file ends early — it was cut off while being sent or saved. Everything before the cut has been read; the last attachment may be incomplete.')))
  if (msg.checksumBad) notes.append(el('p', 'wm-note-line wm-warn', t('note_checksum', 'Some parts of the file fail its own integrity check, so it was damaged on the way. The files are given as they are — check them before relying on them.')))
  if (msg.cpFallback) notes.append(el('p', 'wm-note-line', t('note_codepage', 'The file names an unusual text encoding; names may show some wrong letters.')))

  let title, line, tone = 'ok'
  if (c.files || c.letters) {
    title = t('verdict_ok', 'winmail.dat opened')
    line = t('body_ok', 'Files inside: :n. :letter', { n: c.files + c.letters, letter: hasText ? t('body_ok_text', 'The letter text is below.') : '' }).trim()
  } else if (hasText || msg.meeting) {
    title = t('verdict_text_only', 'winmail.dat opened — there are no files in it')
    line = msg.meeting ? t('body_meeting_only', 'It is a calendar invitation; its details and text are below.') : t('body_text_only', 'Outlook packed only the letter itself here: its formatting and text. Nothing was lost — there were no attachments.')
  } else {
    title = t('verdict_empty', 'winmail.dat opened, but it holds nothing to show')
    line = t('body_empty', 'There is neither text nor attachments in it — only service data for Outlook.')
    tone = 'warn'
  }
  status(title, line, tone, notes.childElementCount ? notes : null)
  renderLetter(msg, out, 0)

  try {
    window.__aeToolComplete && window.__aeToolComplete({ files: c.files, letters: c.letters, body: body.source || 'none', meeting: msg.meeting ? 1 : 0 })
  } catch (_) {}
  stage('complete')
  try {
    reportShape({
      format: 'tnef', ok: true, size: file.size, version: String(msg.codepage || 0),
      reason: where.via || 'file',
      flags: [].concat(msg.flags || [], body.rtfKind ? ['rtf-' + body.rtfKind] : [], body.source ? ['body-' + body.source] : [], msg.meeting ? ['meeting'] : []).slice(0, 12),
      counts: { files: c.files, letters: c.letters, attrs: msg.attrCount, badsum: msg.checksumBad },
    })
  } catch (_) {}
}

export function init() {
  const zone = document.getElementById('wmDrop')
  if (!zone) return
  // __bindUploaderFile hands over the file; a plain listener hands over the event.
  const onFile = (x) => {
    const f = x && x.detail ? x.detail.file : x
    if (!f) return
    stage('file_chosen', { mb: Math.round((f.size || 0) / 1048576) })
    open(f).catch((err) => {
      try { window.__handleError && window.__handleError(err, 'winmail_open', { say: t('err_read', 'The file could not be read.') }) } catch (_) {}
    })
  }
  if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, onFile)
  else zone.addEventListener('tool-uploader:file', onFile)
  zone.addEventListener('tool-uploader:clear', clear)
}

window.__wmBoot = 1
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
else init()
